'use client';
import React, { FC, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

type Award = {
  title: string;
  year: string;
  link?: string;
};

type SectionsAwardsAndRecognitionProps = {
  data: {
    __typename?: string;
    heading?: string;
    view_btn_label?: string;
    padding: {
      mobile_top_padding: any;
      mobile_bottom_padding: any;
      desktop_top_padding: any;
      desktop_bottom_padding: any;
    };
    awards_list: Award[];
  };
};

const ComponentSectionsAwardsAndRecognition: FC<SectionsAwardsAndRecognitionProps> = ({ data }) => {
  const { __typename, heading, view_btn_label = 'View', awards_list = [] } = data;
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <section
      id={__typename}
      className="border-t border-[#B6C38066] pt-4 md:pt-5 awards-sec"
      style={{
        paddingTop: `${top}rem`,
        paddingBottom: `${bottom}rem`,
      }}
    >
      <div className="grid grid-cols-12 items-start lg:gap-0">
        {/* Animated Heading */}
        <motion.div
          className="col-span-12 lg:col-span-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <h3 className="text-[20px] md:text-[22px] text-[#E5E1D5] font-light leading-none lg:mb-5 mb-7">
            {heading}
          </h3>
        </motion.div>

        {/* Animated Table */}
        <motion.div
          className="col-span-12 lg:col-span-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <table className="w-full text-[15px] md:text-[16px] leading-9 font-light">
            <tbody>
              {awards_list.map((award: Award, index: number) => (
                <tr
                  key={index}
                  className="border-b border-[#b6c38066] text-[#E5E1D5] hover:border-[#B6C380] relative cursor-pointer transition-all hover:text-[#B6C380]"
                >
                  <td>{award.title}</td>
                  <td>{award.year}</td>
                  <td className="text-right view">
                    {award.link ? (
                      <a
                        href={award.link}
                        className="text-base leading-none"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {view_btn_label}
                      </a>
                    ) : (
                      <span>{view_btn_label}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
};

export default ComponentSectionsAwardsAndRecognition;
